import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Star, ShieldCheck, UserCheck, FileText, Bell, MonitorSmartphone, CheckCircle2, ArrowRight, ArrowUpRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getServices, getProjects, getTestimonials, getBlogs, getSite } from "@/lib/api";
import { Reveal, MaskLines } from "@/lib/motion";
import LeadForm from "@/components/LeadForm";
import LeadWizard from "@/components/LeadWizard";
import { useSeo } from "@/lib/useSeo";
import { BUSINESS } from "@/lib/config";

const HERO_IMG = "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?crop=entropy&cs=srgb&fm=jpg&q=90&w=2000";
const PORTAL_IMG = "https://images.pexels.com/photos/33685861/pexels-photo-33685861.jpeg?auto=compress&cs=tinysrgb&w=1400";

const COLLECTIONS = [
  {
    id: "modern-luxury",
    number: "01",
    name: "Modern Luxury",
    tagline: "Sculpted lines, restrained warmth",
    body: "Architectural clarity meets sensual materials — walnut veneers, honed stone, brushed brass. Made for those who want their space to feel edited, never emptied.",
    image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1400&q=85",
  },
  {
    id: "quiet-luxury",
    number: "02",
    name: "Quiet Luxury",
    tagline: "Whispered opulence, tactile calm",
    body: "The luxury of restraint. Soft ivory palettes, hand-tufted textiles and a considered emptiness that lets each object breathe. For homes that prefer to speak softly.",
    image: "https://images.unsplash.com/photo-1618219740975-d40978bb7378?auto=format&fit=crop&w=1400&q=85",
  },
  {
    id: "contemporary-classic",
    number: "03",
    name: "Contemporary Classic",
    tagline: "Heritage silhouettes, modern grace",
    body: "Fluted joinery, arched doorways and enduring proportions rendered in a modern hand. A composition of memory and today — timeless without being predictable.",
    image: "https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&w=1400&q=85",
  },
];

const WHY = [
  { icon: UserCheck, t: "One Dedicated Studio Lead", d: "A single principal owns your project — from first sketch to final styling." },
  { icon: FileText, t: "Transparent Bill of Quantities", d: "Every joint, every fabric, every finish, itemised. No hidden line items." },
  { icon: Bell, t: "Daily Site Progress", d: "Curated photographs and status arrive on your phone, every evening." },
  { icon: ShieldCheck, t: "Ten-Year Craft Warranty", d: "Modular joinery and cabinetry backed by a decade-long warranty." },
];

const PROCESS = [
  { n: "I", t: "Discovery", pay: "", d: "A studio conversation about how you live, what you love, and what you would rather leave behind." },
  { n: "II", t: "Reserve", pay: "5% payment", d: "Reserve your studio slot. You are assigned a dedicated project principal within 48 hours." },
  { n: "III", t: "Design", pay: "25% payment", d: "Concepts, 3D walkthroughs and a fully transparent BOQ — signed off before a single line is drawn on site." },
  { n: "IV", t: "Execute", pay: "60% payment", d: "Turnkey build with daily photography, milestone approvals and complete oversight from the studio." },
  { n: "V", t: "Handover", pay: "10% payment", d: "Styled, cleaned, and quality-checked. Every surface, every seam, ready for the life you're about to live." },
];

const FAQS = [
  ["How long does a full residence take?", "Most private residences complete within 45–90 days depending on scope. Your principal shares a detailed timeline at design sign-off."],
  ["How transparent is the pricing?", "Every project includes a detailed, line-by-line BOQ. You know precisely what each finish, fitting and finishing hand-off costs — before we begin."],
  ["Do you handle turnkey execution?", "Yes. From civil work and false ceilings to modular joinery and final styling — everything is delivered under one studio."],
  ["What warranty do you offer?", "We provide up to a 10-year warranty on modular joinery and comprehensive service support after handover."],
];

export default function Home() {
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: getSite });
  useSeo({ title: "", description: BUSINESS.tagline });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "8%"]);

  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: getServices });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => getProjects() });
  const { data: testimonials = [] } = useQuery({ queryKey: ["testimonials"], queryFn: getTestimonials });
  const { data: blogs = [] } = useQuery({ queryKey: ["blogs"], queryFn: () => getBlogs() });

  const featured = projects.slice(0, 5);
  const heroImage = site?.hero_image || HERO_IMG;

  return (
    <div data-testid="home-page" className="bg-warm-ivory">
      {/* ================= SPLIT HERO ================= */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-[100svh] bg-espresso text-warm-ivory overflow-hidden">
        <div className="relative z-10 min-h-[100svh] mx-auto max-w-[1500px] grid lg:grid-cols-2 pt-24 lg:pt-0">
          {/* LEFT — editorial dark composition */}
          <div className="relative flex flex-col justify-center px-6 md:px-12 lg:px-16 py-16 lg:py-0 order-2 lg:order-1">
            {/* champagne hairline + eyebrow */}
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <span className="champagne-rule" />
                <span className="fine-uppercase text-[0.68rem] text-champagne">Bespoke Luxury Interiors</span>
              </div>
            </Reveal>

            <h1 className="font-display font-normal leading-[0.98] tracking-[-0.015em] text-[2.6rem] sm:text-[3.4rem] md:text-[4.2rem] lg:text-[4.6rem] xl:text-[5.2rem]">
              <MaskLines lines={["SPACES THAT", "REFLECT YOU"]} delay={0.35} />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="mt-10 max-w-md text-warm-ivory/65 text-[1.02rem] leading-[1.75] font-light"
            >
              Kolkata's boutique interior atelier for private residences. A studio of a few — never a factory of many — where every project is a portrait of the people who will live in it.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.8 }}
              className="mt-12 flex flex-wrap items-center gap-8"
            >
              <Link
                to="/book-consultation"
                data-testid="hero-consult-btn"
                className="group inline-flex items-center gap-3 bg-champagne text-espresso fine-uppercase text-[0.72rem] px-8 py-4 hover:bg-warm-ivory transition-colors duration-500"
              >
                Private Consultation
                <ArrowRight size={14} strokeWidth={1.6} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/gallery"
                data-testid="hero-gallery-btn"
                className="group inline-flex items-center gap-3 fine-uppercase text-[0.72rem] text-warm-ivory/80 hover:text-champagne transition-colors"
              >
                <span className="link-underline pb-1">View Our Work</span>
                <ArrowUpRight size={14} strokeWidth={1.6} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </motion.div>

            {/* fine studio meta at bottom */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6, duration: 0.8 }}
              className="hidden lg:flex mt-24 gap-16 pt-8"
              style={{ borderTop: "1px solid rgba(180,154,106,0.22)" }}
            >
              <div>
                <p className="font-display text-3xl text-warm-ivory">120<span className="text-champagne">+</span></p>
                <p className="fine-uppercase text-[0.62rem] text-warm-ivory/50 mt-1">Homes styled</p>
              </div>
              <div>
                <p className="font-display text-3xl text-warm-ivory">10 <span className="text-champagne italic font-light">yr</span></p>
                <p className="fine-uppercase text-[0.62rem] text-warm-ivory/50 mt-1">Craft warranty</p>
              </div>
              <div>
                <p className="font-display text-3xl text-warm-ivory">45–90</p>
                <p className="fine-uppercase text-[0.62rem] text-warm-ivory/50 mt-1">Days to handover</p>
              </div>
            </motion.div>
          </div>

          {/* RIGHT — image + LeadWizard overlay (option a) */}
          <div className="relative min-h-[70vh] lg:min-h-[100svh] order-1 lg:order-2 overflow-hidden">
            <motion.div style={{ scale: imgScale, y: imgY }} className="absolute inset-0">
              <img src={heroImage} alt="Sutra Interio residence" className="w-full h-full object-cover" />
              {/* rich dark overlay for form readability */}
              <div className="absolute inset-0 bg-gradient-to-b from-espresso/40 via-espresso/25 to-espresso/70" />
              <div className="absolute inset-0 bg-gradient-to-r from-espresso/60 via-transparent to-transparent lg:from-espresso lg:via-espresso/0 lg:to-transparent" style={{ backgroundImage: "linear-gradient(to left, transparent 0%, rgba(41,35,31,0.0) 40%, rgba(41,35,31,0.9) 100%)" }} />
            </motion.div>

            {/* corner index tag */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.8 }}
              className="absolute top-8 right-8 hidden md:flex items-center gap-3 text-warm-ivory/70"
            >
              <span className="fine-uppercase text-[0.62rem]">Studio № 001</span>
              <span className="champagne-rule" style={{ width: "1.5rem" }} />
              <span className="fine-uppercase text-[0.62rem]">Kolkata</span>
            </motion.div>

            {/* Lead wizard floating card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9 }}
              className="absolute inset-0 flex items-center justify-center lg:justify-end p-6 md:p-10 lg:pr-16"
            >
              <div className="w-full max-w-sm md:max-w-md">
                <LeadWizard source="Hero Consultation Form" luxe />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= COLLECTIONS ================= */}
      <section className="bg-warm-ivory py-28 md:py-40">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 mb-20">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="flex items-center gap-4 mb-6">
                  <span className="champagne-rule" />
                  <span className="fine-uppercase text-[0.68rem] text-champagne">The Collections</span>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display text-5xl md:text-6xl leading-[1.02] text-espresso">
                  Three design<br /><span className="italic font-normal text-walnut">philosophies.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 flex items-end">
              <Reveal delay={0.1}>
                <p className="text-espresso/70 text-lg leading-[1.75] font-light">
                  Not price tiers. Not packages. These are three ways of thinking about a home — three languages we use to translate how you want to live into what surrounds you.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {COLLECTIONS.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.1}>
                <Link to="/services" data-testid={`collection-${c.id}`} className="group block">
                  <div className="relative overflow-hidden">
                    <img src={c.image} alt={c.name} className="w-full h-[440px] md:h-[520px] object-cover transition-transform duration-[1400ms] group-hover:scale-[1.02]" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 via-espresso/10 to-transparent" />
                    <div className="absolute top-6 left-6 flex items-center gap-3 text-warm-ivory">
                      <span className="fine-uppercase text-[0.62rem] text-champagne">{c.number}</span>
                      <span className="champagne-rule" style={{ width: "1.25rem" }} />
                    </div>
                  </div>
                  <div className="mt-6">
                    <h3 className="font-display text-3xl text-espresso group-hover:text-walnut transition-colors">{c.name}</h3>
                    <p className="fine-uppercase text-[0.62rem] text-champagne mt-2">{c.tagline}</p>
                    <p className="mt-4 text-espresso/65 text-[0.98rem] leading-[1.75] font-light">{c.body}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= WHY / STUDIO CREDO ================= */}
      <section className="relative bg-soft-ivory py-28 md:py-40">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="flex items-center gap-4 mb-6">
                  <span className="champagne-rule" />
                  <span className="fine-uppercase text-[0.68rem] text-champagne">The Studio Way</span>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display text-5xl md:text-6xl leading-[1.02] text-espresso">
                  Boutique by choice,<br /><span className="italic text-walnut font-normal">not by scale.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 text-espresso/70 text-lg leading-[1.75] font-light max-w-md">
                  We take on a small number of homes each year — the kind of number that lets us be present in every room, every material meeting, every difficult decision. Volume is not our ambition; quality of attention is.
                </p>
                <div className="mt-10">
                  <Link to="/about" data-testid="why-about-link" className="group inline-flex items-center gap-3 fine-uppercase text-[0.72rem] text-espresso">
                    <span className="link-underline pb-1">Our Story</span>
                    <ArrowUpRight size={14} strokeWidth={1.6} className="text-champagne group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </Link>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 grid sm:grid-cols-2 gap-6">
              {WHY.map((w, i) => (
                <Reveal key={w.t} delay={i * 0.08}>
                  <div className="bg-warm-ivory p-8 h-full hairline border">
                    <w.icon className="text-champagne" size={22} strokeWidth={1.3} />
                    <h3 className="font-display text-2xl mt-5 text-espresso">{w.t}</h3>
                    <p className="text-espresso/65 mt-2 text-[0.96rem] leading-[1.75] font-light">{w.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= SERVICES ================= */}
      <section className="bg-warm-ivory py-28 md:py-40">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <Reveal>
                <div className="flex items-center gap-4 mb-6">
                  <span className="champagne-rule" />
                  <span className="fine-uppercase text-[0.68rem] text-champagne">Studio Services</span>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display text-5xl md:text-6xl leading-[1.02] text-espresso max-w-xl">
                  Everything for the<br /><span className="italic font-normal text-walnut">life within.</span>
                </h2>
              </Reveal>
            </div>
            <Reveal>
              <Link to="/services" data-testid="services-all-link" className="group inline-flex items-center gap-3 fine-uppercase text-[0.72rem] text-espresso">
                <span className="link-underline pb-1">All Services</span>
                <ArrowUpRight size={14} strokeWidth={1.6} className="text-champagne group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 4) * 0.08}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`}
                  className="group block bg-soft-ivory overflow-hidden hairline border hover:border-champagne transition-colors duration-500">
                  <div className="overflow-hidden h-56">
                    <img src={s.hero_image} alt={s.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms]" loading="lazy" />
                  </div>
                  <div className="p-7">
                    <span className="fine-uppercase text-[0.62rem] text-champagne">0{i + 1}</span>
                    <h3 className="font-display text-2xl mt-2 text-espresso">{s.title}</h3>
                    <p className="text-espresso/60 text-[0.96rem] mt-2 leading-[1.7] font-light">{s.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PROCESS ================= */}
      <section className="bg-espresso text-warm-ivory py-28 md:py-40 relative grain overflow-hidden">
        <div className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-12">
          <div className="text-center mb-20">
            <Reveal>
              <div className="inline-flex items-center gap-4 mb-6">
                <span className="champagne-rule" />
                <span className="fine-uppercase text-[0.68rem] text-champagne">Our Process</span>
                <span className="champagne-rule" />
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-5xl md:text-6xl leading-[1.02]">
                Five chapters,<br /><span className="italic text-champagne font-normal">one composed journey.</span>
              </h2>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-6">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div className="relative">
                  <span className="font-display text-6xl md:text-7xl text-champagne/40 leading-none italic font-light">{p.n}</span>
                  <h3 className="font-display text-2xl mt-3 text-warm-ivory">{p.t}</h3>
                  {p.pay && (
                    <span className="inline-block mt-3 border border-champagne/50 text-champagne text-[0.6rem] fine-uppercase px-3 py-1">{p.pay}</span>
                  )}
                  <p className="text-warm-ivory/65 text-[0.94rem] mt-4 leading-[1.7] font-light">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-20 text-center">
            <Link to="/book-consultation" data-testid="process-book-btn"
              className="group inline-flex items-center gap-3 border border-champagne text-champagne fine-uppercase text-[0.72rem] px-9 py-4 hover:bg-champagne hover:text-espresso transition-colors duration-500">
              Begin the Conversation
              <ArrowRight size={14} strokeWidth={1.6} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ================= FEATURED PROJECTS ================= */}
      <section className="bg-warm-ivory py-28 md:py-40">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <Reveal>
                <div className="flex items-center gap-4 mb-6">
                  <span className="champagne-rule" />
                  <span className="fine-uppercase text-[0.68rem] text-champagne">Recent Work</span>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display text-5xl md:text-6xl leading-[1.02] text-espresso">
                  Selected<br /><span className="italic font-normal text-walnut">projects.</span>
                </h2>
              </Reveal>
            </div>
            <Reveal>
              <Link to="/gallery" data-testid="featured-gallery-link" className="group inline-flex items-center gap-3 fine-uppercase text-[0.72rem] text-espresso">
                <span className="link-underline pb-1">View Full Gallery</span>
                <ArrowUpRight size={14} strokeWidth={1.6} className="text-champagne" />
              </Link>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {featured.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 2) * 0.1} className={i === 0 ? "md:row-span-2" : ""}>
                <Link to="/gallery" data-testid={`featured-project-${p.slug}`} className="group block overflow-hidden relative">
                  <img src={p.image} alt={p.title} className={`w-full object-cover group-hover:scale-[1.02] transition-transform duration-[1400ms] ${i === 0 ? "h-[300px] md:h-[720px]" : "h-[300px] md:h-[350px]"}`} loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-espresso/85 via-espresso/20 to-transparent" />
                  <div className="absolute bottom-0 p-8 text-warm-ivory">
                    <span className="fine-uppercase text-[0.62rem] text-champagne">{p.category}</span>
                    <h3 className="font-display text-3xl md:text-4xl mt-2">{p.title}</h3>
                    <p className="text-warm-ivory/65 text-sm mt-1 font-light">{p.location}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CLIENT PORTAL ================= */}
      <section className="bg-soft-ivory py-28 md:py-40">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <Reveal>
              <div className="flex items-center gap-4 mb-6">
                <span className="champagne-rule" />
                <span className="fine-uppercase text-[0.68rem] text-champagne">The Studio Portal</span>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-5xl md:text-6xl leading-[1.02] text-espresso">
                Every decision,<br /><span className="italic font-normal text-walnut">in your pocket.</span>
              </h2>
            </Reveal>
            <div className="mt-10 space-y-6">
              {[
                [MonitorSmartphone, "Live Progress Dashboard", "Track designs, materials and timelines from your phone, any hour."],
                [CheckCircle2, "Digital Approvals", "Sign off on drawings and finishes with a single tap — no printouts."],
                [Bell, "Daily Photograph Feed", "Site photographs and milestone notes pushed to you every evening."],
              ].map(([Icon, t, d], i) => (
                <Reveal key={t} delay={i * 0.08} className="flex gap-4">
                  <Icon className="text-champagne shrink-0 mt-1" size={20} strokeWidth={1.3} />
                  <div>
                    <h3 className="font-display text-xl text-espresso">{t}</h3>
                    <p className="text-espresso/65 text-[0.96rem] mt-1 font-light leading-relaxed">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.1}>
            <div className="relative luxe-shadow">
              <img src={PORTAL_IMG} alt="Client portal preview" className="w-full h-[460px] md:h-[560px] object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-espresso/25" />
              <div className="absolute top-6 left-6 bg-warm-ivory text-espresso px-6 py-4">
                <p className="fine-uppercase text-[0.6rem] text-champagne">Project Progress</p>
                <p className="font-display text-3xl mt-1">78<span className="text-champagne italic font-light">%</span></p>
              </div>
              <div className="absolute bottom-6 right-6 bg-espresso text-warm-ivory px-6 py-4">
                <p className="fine-uppercase text-[0.6rem] text-champagne">Handover in</p>
                <p className="font-display text-2xl mt-1">27 <span className="text-warm-ivory/60 text-sm">days</span></p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="bg-warm-ivory py-28 md:py-40">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <div className="text-center mb-20">
            <Reveal>
              <div className="inline-flex items-center gap-4 mb-6">
                <span className="champagne-rule" />
                <span className="fine-uppercase text-[0.68rem] text-champagne">Homeowner Voices</span>
                <span className="champagne-rule" />
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-5xl md:text-6xl leading-[1.02] text-espresso">
                Kind words, from<br /><span className="italic font-normal text-walnut">the homes we've styled.</span>
              </h2>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t, i) => (
              <Reveal key={t.name} delay={i * 0.08} className="bg-soft-ivory p-8 hairline border">
                <div className="flex gap-1 text-champagne">{Array(t.rating).fill(0).map((_, k) => <Star key={k} size={14} fill="currentColor" />)}</div>
                <p className="font-display text-xl md:text-[1.4rem] leading-[1.4] mt-5 text-espresso italic">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-8 pt-6 hairline border-t flex items-center gap-3">
                  <span className="champagne-rule" style={{ width: "1.5rem" }} />
                  <div>
                    <p className="font-display text-espresso text-lg leading-none">{t.name}</p>
                    <p className="text-espresso/50 text-xs mt-1 fine-uppercase">{t.location}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-16 text-center">
            <Link to="/testimonials" data-testid="all-testimonials-link" className="group inline-flex items-center gap-3 fine-uppercase text-[0.72rem] text-espresso">
              <span className="link-underline pb-1">All Testimonials</span>
              <ArrowUpRight size={14} strokeWidth={1.6} className="text-champagne" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ================= JOURNAL ================= */}
      <section className="bg-soft-ivory py-28 md:py-40">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <Reveal>
                <div className="flex items-center gap-4 mb-6">
                  <span className="champagne-rule" />
                  <span className="fine-uppercase text-[0.68rem] text-champagne">The Journal</span>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display text-5xl md:text-6xl leading-[1.02] text-espresso">
                  Latest<br /><span className="italic font-normal text-walnut">reflections.</span>
                </h2>
              </Reveal>
            </div>
            <Reveal>
              <Link to="/blog" data-testid="all-blogs-link" className="group inline-flex items-center gap-3 fine-uppercase text-[0.72rem] text-espresso">
                <span className="link-underline pb-1">All Articles</span>
                <ArrowUpRight size={14} strokeWidth={1.6} className="text-champagne" />
              </Link>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {blogs.slice(0, 3).map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.08}>
                <Link to={`/blog/${b.slug}`} data-testid={`home-blog-${b.slug}`} className="group block">
                  <div className="overflow-hidden h-64 mb-6">
                    <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms]" loading="lazy" />
                  </div>
                  <span className="fine-uppercase text-[0.6rem] text-champagne">{b.category} · {b.read_time}</span>
                  <h3 className="font-display text-2xl mt-2 text-espresso group-hover:italic transition-all">{b.title}</h3>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="bg-warm-ivory py-28 md:py-40">
        <div className="mx-auto max-w-[900px] px-6 md:px-12">
          <div className="text-center mb-16">
            <Reveal>
              <div className="inline-flex items-center gap-4 mb-6">
                <span className="champagne-rule" />
                <span className="fine-uppercase text-[0.68rem] text-champagne">Questions</span>
                <span className="champagne-rule" />
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-5xl md:text-6xl leading-[1.02] text-espresso">
                Frequently<br /><span className="italic font-normal text-walnut">asked.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map(([q, a], i) => (
                <AccordionItem key={i} value={`f${i}`} className="border-b hairline">
                  <AccordionTrigger className="font-display text-xl md:text-2xl text-left hover:no-underline py-6 text-espresso">{q}</AccordionTrigger>
                  <AccordionContent className="text-espresso/65 text-[1rem] leading-[1.75] pb-6 font-light">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ================= FINAL CONTACT ================= */}
      <section id="enquire" className="bg-espresso text-warm-ivory grain relative py-28 md:py-40">
        <div className="relative z-10 mx-auto max-w-[1100px] px-6 md:px-12">
          <div className="text-center mb-16">
            <Reveal>
              <div className="inline-flex items-center gap-4 mb-6">
                <span className="champagne-rule" />
                <span className="fine-uppercase text-[0.68rem] text-champagne">Start Your Project</span>
                <span className="champagne-rule" />
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-5xl md:text-6xl leading-[1.02]">
                Tell us about your<br /><span className="italic text-champagne font-normal">dream space.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal className="mt-12 bg-warm-ivory text-espresso p-8 md:p-14 luxe-shadow">
            <LeadForm variant="full" source="Homepage Enquiry" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
